import { fetchDelete, fetchGet, fetchPost, fetchPut } from './fetch';
import {
  AudioCheckExistResponse,
  AudioDetailRequest,
  AudioDetailResponse,
  AudioListRequest,
  AudioListResponse,
  AudioListV2Request,
  CatalogOverviewResponse,
  GetAudioDownloadResponse,
  GetAudioDownloadStemsResponse,
  GetGenerateStatusRequest,
  GetPeaksResponse,
  GetSignUrlRequest,
  GetSignUrlResponse,
  TrackGroupResponse,
  UpdateDetailRequest,
  UpdateDetailResponse,
} from './interface';

const API_URL = '';

/**
 * formatName: <Method(get|post)><ServiceName><ActionName>
 */
export const getAudioTrackGroup = async (): Promise<TrackGroupResponse> => {
  const url = `${API_URL}/api/v1/audio/track_group`;
  return fetchGet<TrackGroupResponse>(url);
};

export const getGroupAudio = async (req?: AudioListRequest): Promise<AudioListResponse> => {
  const url = `${API_URL}/api/v1/audio/group_audio`;
  return fetchGet<AudioListResponse>(url, undefined, req);
};

export const getAudioAllTracks = async (req?: AudioListRequest): Promise<AudioListResponse> => {
  const url = `${API_URL}/api/v1/audio/all_tracks/:lastId`;
  return fetchGet<AudioListResponse>(url, undefined, req);
};

export const getAudioAllTracksV2 = async (queryParams?: AudioListV2Request): Promise<AudioListResponse> => {
  const url = `${API_URL}/api/v2/audio/all_tracks`;
  return fetchGet<AudioListResponse>(url, {}, queryParams);
};

export const getAudioLikedTracks = async (req?: AudioListRequest): Promise<AudioListResponse> => {
  const url = `${API_URL}/api/v1/audio/liked_track/:lastId`;
  return fetchGet<AudioListResponse>(url, undefined, req);
};

export const getAudioPublishedTracks = async (req?: AudioListRequest): Promise<AudioListResponse> => {
  const url = `${API_URL}/api/v1/audio/published_tracks/:lastId`;
  return fetchGet<AudioListResponse>(url, undefined, req);
};

export const getAudioTrendingTrack = async (req?: AudioListRequest): Promise<AudioListResponse> => {
  const url = `${API_URL}/api/v1/audio/trending_track`;
  return fetchGet<AudioListResponse>(url, undefined, req);
};

export const getAudioRecentlyTrack = async (req?: AudioListRequest): Promise<AudioListResponse> => {
  const url = `${API_URL}/api/v1/audio/recently_track`;
  return fetchGet<AudioListResponse>(url, undefined, req);
};

export const getAudioAllPublishedTrack = async (req?: AudioListRequest): Promise<AudioListResponse> => {
  const url = `${API_URL}/api/v1/audio/all_published_tracks`;
  return fetchGet<AudioListResponse>(url, undefined, req);
};

export const getCatalogOverview = async (): Promise<CatalogOverviewResponse> => {
  const url = `${API_URL}/api/v2/audio/catalog_overview`;
  return fetchGet<CatalogOverviewResponse>(url);
};

export const getAudioDetail = async (req?: AudioDetailRequest): Promise<AudioDetailResponse> => {
  const { id } = req ?? {};
  const url = `${API_URL}/api/v2/audio/detail/${id}`;
  return fetchGet<AudioDetailResponse>(url);
};

export const getSignUrl = async (req?: GetSignUrlRequest): Promise<GetSignUrlResponse> => {
  const url = `${API_URL}/api/v1/audio/sign_url`;
  return fetchPost<GetSignUrlRequest, GetSignUrlResponse>(url, req);
};

export const getUploadSignUrl = async (req: GetSignUrlRequest): Promise<GetSignUrlResponse> => {
  const { id } = req ?? {};
  const url = `${API_URL}/api/v1/audio/sign_url_for_upload_file/${id}`;
  return fetchGet<GetSignUrlResponse>(url);
};

export const bootstrap = async (): Promise<Record<string, unknown>> => {
  const url = `${API_URL}/api/auth/info`;
  return fetchGet<Record<string, unknown>>(url);
};

export const logout = async (): Promise<void> => {
  const url = `${API_URL}/api/auth/logout`;
  return fetchGet<void>(url);
};

export const getGenerateStatus = async (req?: GetGenerateStatusRequest): Promise<AudioCheckExistResponse> => {
  const { id } = req ?? {};
  const url = `${API_URL}/api/v2/audio/generation_status/${id}`;
  return fetchGet<AudioCheckExistResponse>(url, undefined, req);
};

export const updateDetail = async (req?: UpdateDetailRequest): Promise<UpdateDetailResponse> => {
  const url = `${API_URL}/api/v1/audio/update_detail`;
  return fetchPut<UpdateDetailRequest, UpdateDetailResponse>(url, req);
};

interface PublishResponse {
  data?: Record<string, never>;
}

export const publishAudio = async (req?: { id: string }): Promise<PublishResponse> => {
  const url = `${API_URL}/api/v1/audio/publish_audio`;
  return fetchPost<{ id: string }, PublishResponse>(url, req);
};

export const unPublishAudio = async (req?: { id: string }): Promise<PublishResponse> => {
  const url = `${API_URL}/api/v1/audio/unpublish_audio`;
  return fetchDelete<{ id: string }, PublishResponse>(url, req);
};

export const deleteAudio = async (req?: { id: string }) => {
  const url = `${API_URL}/api/v1/audio/delete_audio`;
  return fetchDelete<{ id: string }, { data?: object }>(url, req);
};

export const updatePlayTime = async (req: { playingId: string }): Promise<Record<string, unknown>> => {
  const url = `${API_URL}/api/v1/audio/playtime/:playingId`;
  return fetchPost<{ playingId: string }, Record<string, unknown>>(url, undefined, req);
};

export const getDownloadUrl = async (req?: { id: string }): Promise<GetAudioDownloadResponse> => {
  const { id } = req ?? {};
  const url = `${API_URL}/api/v2/audio/download/${id}`;
  return fetchGet<GetAudioDownloadResponse>(url, req);
};

export const getPeaks = async (req?: { songId: string }): Promise<GetPeaksResponse> => {
  const url = `${API_URL}/api/v2/audio/peaks/:songId`;
  return fetchGet<GetPeaksResponse>(url, req);
};

export const getDownloadStemsUrl = async (req?: { id: string }): Promise<GetAudioDownloadStemsResponse> => {
  const { id } = req ?? {};
  const url = `${API_URL}/api/v2/audio/download_stems/${id}`;
  return fetchGet<GetAudioDownloadStemsResponse>(url, req);
};
